/**
 * ApplicationsSection
 * Tabla de solicitudes del cliente. Filtra client-side por directoryId.
 * Click en una fila abre ApplicationDetailModal.
 */
import { useState, useMemo } from 'react';
import { type ColumnDef } from '@tanstack/react-table';
import { Badge } from '@/components/ui/Badge';
import { CreditLevelBadge } from '@/features/credit/components/CreditLevelBadge';
import { DataTable } from '@/features/credit/components/DataTable';
import { ApplicationDetailModal } from '@/features/credit/components/ApplicationDetailModal';
import { useBackofficeApplications } from '@/features/credit/hooks/useBackofficeApplications';
import { formatCurrency } from '@/utils/formatters';
import { CreditLevel } from '@/types/credit.types';
import type { BackofficeApplicationsListItem } from '@/types/backoffice-crud.types';

interface ApplicationsSectionProps {
  directoryId: string;
}

export function ApplicationsSection({ directoryId }: ApplicationsSectionProps) {
  const [selectedApplicationId, setSelectedApplicationId] = useState<string | null>(null);
  const { applications, isLoading, isError } = useBackofficeApplications();

  const clientApps = useMemo(
    () => applications.filter((a) => a.directory_id === directoryId),
    [applications, directoryId],
  );

  if (isLoading) return <div className="p-4 text-sm text-muted-foreground">Cargando solicitudes…</div>;
  if (isError) return <div className="p-4 text-sm text-red-600 dark:text-red-400">Error al cargar solicitudes.</div>;

  return (
    <div className="p-4">
      {clientApps.length === 0 ? (
        <p className="text-sm text-muted-foreground py-2">Este cliente no tiene solicitudes.</p>
      ) : (
        <DataTable
          data={clientApps}
          columns={appColumns}
          searchPlaceholder="Buscar por ID de solicitud..."
          emptyMessage="Sin solicitudes"
          pageSize={5}
          onRowClick={(a) => setSelectedApplicationId(a.application_id)}
        />
      )}

      {selectedApplicationId && (
        <ApplicationDetailModal
          isOpen
          applicationId={selectedApplicationId}
          directoryId={directoryId}
          onClose={() => setSelectedApplicationId(null)}
        />
      )}
    </div>
  );
}

// ── helpers ─────────────────────────────────────────────────────────────

function formatDateShort(dateStr: string): string {
  if (!dateStr) return '-';
  try {
    return new Intl.DateTimeFormat('es-MX', { day: '2-digit', month: 'short', year: 'numeric' }).format(new Date(dateStr));
  } catch { return dateStr; }
}

function truncateId(id: string): string {
  return id.length > 8 ? `${id.slice(0, 8)}...` : id;
}

type AppBadgeVariant = 'default' | 'success' | 'warning' | 'danger' | 'info';

function getAppStatusVariant(status: string): AppBadgeVariant {
  switch (status) {
    case 'SIGNED': case 'DISBURSED': return 'success';
    case 'PENDING_SIGNATURE': case 'CREATED': return 'warning';
    case 'EXPIRED': case 'CANCELLED': return 'danger';
    default: return 'default';
  }
}

const appColumns: ColumnDef<BackofficeApplicationsListItem, unknown>[] = [
  { accessorKey: 'created_at', header: 'Creada', cell: ({ getValue }) => formatDateShort(getValue<string>()) },
  { accessorKey: 'signed_at', header: 'Firmada', cell: ({ getValue }) => { const v = getValue<string>(); return v ? formatDateShort(v) : 'Pendiente'; } },
  {
    accessorKey: 'application_id', header: 'ID',
    cell: ({ getValue }) => <span className="font-mono text-xs" title={getValue<string>()}>{truncateId(getValue<string>())}</span>,
  },
  {
    accessorKey: 'credit_level', header: 'Nivel',
    cell: ({ getValue }) => {
      const level = getValue<string | undefined>();
      if (!level) return '-';
      const isValid = Object.values(CreditLevel).includes(level as CreditLevel);
      return isValid ? <CreditLevelBadge level={level as CreditLevel} size="sm" /> : <span className="text-xs">{level}</span>;
    },
  },
  {
    accessorKey: 'amount_mxn', header: 'Monto',
    cell: ({ getValue }) => <span className="tabular-nums">{formatCurrency(getValue<number>(), 'MXN', 'es-MX')}</span>,
  },
  { accessorKey: 'term_weeks', header: 'Plazo', cell: ({ getValue }) => `${getValue<number>()} sem` },
  {
    accessorKey: 'annual_rate', header: 'Tasa Anual',
    cell: ({ getValue }) => {
      const rate = getValue<number | undefined>();
      return rate != null ? `${(rate * 100).toFixed(1)}%` : '-';
    },
  },
  {
    accessorKey: 'weekly_payment_mxn', header: 'Pago Semanal',
    cell: ({ getValue }) => {
      const payment = getValue<number | undefined>();
      return payment != null ? (
        <span className="tabular-nums">{formatCurrency(payment, 'MXN', 'es-MX')}</span>
      ) : '-';
    },
  },
  {
    accessorKey: 'status', header: 'Estado',
    cell: ({ getValue }) => { const s = getValue<string>(); return <Badge variant={getAppStatusVariant(s)}>{s}</Badge>; },
  },
];
